import { useGetSavedReportsQuery } from "@src/features/reporting/builder/ReportBuilderContainer.generated";
import { useReportingMockData } from "@src/features/reporting/ReportingMockDataContext";
import {
  ReportingViewOverride,
  useReportingViewOverride,
} from "@src/features/reporting/useReportingViewOverride";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import { Ff } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import CaretDownFillIcon from "@src/lib/icons/18px/caret-down-fill";
import PlusIcon from "@src/lib/icons/18px/plus";
import GridIcon from "@src/lib/icons/18px/grid";
import { cn } from "@src/lib/utils/cn";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/lib/ui/Select";
import { Switch } from "@src/lib/ui/Switch";
import Text from "@src/lib/ui/Text";
import SearchIcon from "@src/lib/icons/18px/search";
import XIcon from "@src/lib/icons/18px/x";
import Fuse from "fuse.js";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import {
  ComponentType,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useExpandedSidebarSectionsStore } from "./atoms";

type NavItem = {
  label: string;
  href: string;
  inlined?: boolean;
  keywords?: string[];
};

type NavSection = {
  label: string;
  icon: ComponentType<{ className?: string }>;
  items: NavItem[];
  createAction?: NavItem;
  keywords?: string[];
};

const TrophyIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9.5 11.75C9.5 11.75 9.5 14.688 13.25 15.75H4.75C8.5 14.688 8.5 11.75 8.5 11.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.286 9C1.469 9 1.74999 3.75 1.74999 3.75H4.42949"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.714 9C16.532 9 16.25 3.75 16.25 3.75H13.5705"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.75 2.25C13.1562 8.159 11.583 11.4958 9.23749 11.75H8.76251C6.41701 11.4958 4.8438 8.159 4.25 2.25H13.75Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SidebarEditIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <line
      x1="6.25"
      y1="2.75"
      x2="6.25"
      y2="15.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m16.25,7.1675v-2.4175c0-1.1045-.8954-2-2-2H3.75c-1.1046,0-2,.8955-2,2v8.5c0,1.1045.8954,2,2,2h3.9156"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m13.2959,15.4542l3.1612-3.1612c.3905-.3905.3905-1.0237,0-1.4142l-.5858-.5858c-.3905-.3905-1.0237-.3905-1.4142,0l-3.1612,3.1612-.7958,2.7959,2.7959-.7958Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BulletListIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect
      x="2.75"
      y="2.75"
      width="12.5"
      height="12.5"
      rx="2"
      ry="2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="8.75"
      y1="9"
      x2="12.25"
      y2="9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="8.75"
      y1="6.25"
      x2="12.25"
      y2="6.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="8.75"
      y1="11.75"
      x2="12.25"
      y2="11.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="6" cy="6" r="1" fill="currentColor" />
    <circle cx="6" cy="9" r="1" fill="currentColor" />
    <circle cx="6" cy="12" r="1" fill="currentColor" />
  </svg>
);

const GaugeIcon = ({ className }: { className?: string }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9 14.25C9.966 14.25 10.75 13.466 10.75 12.5C10.75 11.534 9.966 10.75 9 10.75C8.034 10.75 7.25 11.534 7.25 12.5C7.25 13.466 8.034 14.25 9 14.25Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.75 12.25H16.237C16.24 12.167 16.25 12.084 16.25 12C16.25 7.996 13.004 4.75 9 4.75C4.996 4.75 1.75 7.996 1.75 12C1.75 12.084 1.76 12.167 1.763 12.25H4.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.11661 10.9908L6.55701 8.327"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getStaticNavSections = (): NavSection[] => [
  {
    label: "Completion grids",
    icon: GridIcon,
    keywords: ["grid", "completion", "progress", "status"],
    items: [
      {
        label: "Assignments by location",
        href: "/reporting/location-completion-grid",
        keywords: ["location", "store", "site", "check-in", "checkin"],
      },
      {
        label: "Assignments by user",
        href: "/reporting/user-completion-grid",
        keywords: ["user", "trainee", "employee", "person"],
      },
      {
        label: "Checklists",
        href: "/reporting/task-list-completion-grid",
        keywords: ["tasks", "checklists", "to-do"],
      },
      {
        label: "Audits",
        href: "/reporting/most-recent-audit-completion",
        keywords: ["audit", "inspection", "review"],
      },
      {
        label: "Compliance",
        href: "/reporting/compliance",
        keywords: ["compliance", "required", "regulation", "mandatory"],
      },
      {
        label: "Messages",
        href: "/reporting/message-reads-grid",
        keywords: ["message", "blast", "read", "communication"],
      },
    ],
  },
  {
    label: "Leaderboards",
    icon: TrophyIcon,
    keywords: ["ranking", "top", "performance", "scoreboard"],
    items: [
      {
        label: "Locations",
        href: "/reporting/location-leaderboard",
        inlined: true,
        keywords: ["location", "store", "site"],
      },
      {
        label: "Managers",
        href: "/reporting/manager-leaderboard",
        inlined: true,
        keywords: ["manager", "leader", "supervisor"],
      },
      {
        label: "Trainees",
        href: "/reporting/trainee-leaderboard",
        inlined: true,
        keywords: ["trainee", "employee", "new hire"],
      },
    ],
  },
  {
    label: "Activity logs",
    icon: BulletListIcon,
    keywords: ["activity", "log", "history", "submissions"],
    items: [
      {
        label: `Checklist submissions`,
        href: "/reporting/all-task-submissions",
        inlined: true,
        keywords: ["submission", "tasks", "checklist", "completed"],
      },
      {
        label: "Audit submissions",
        href: "/reporting/all-audit-submissions",
        inlined: true,
        keywords: ["submission", "audit", "inspection", "completed"],
      },
      {
        label: "Course feedback",
        href: "/reporting/course-feedback",
        inlined: true,
        keywords: ["feedback", "reviews", "ratings", "survey", "satisfaction"],
      },
      {
        label: "Ask Opus",
        href: "/reporting/ask-opus-answers",
        inlined: true,
        keywords: ["questions", "answers", "AI", "chat"],
      },
      {
        label: "Knowledge Check",
        href: "/reporting/knowledge-check",
        inlined: true,
        keywords: ["quiz", "assessment", "test", "knowledge", "accuracy"],
      },
    ],
  },
];

type SearchableItem = {
  label: string;
  sectionLabel: string;
  keywords: string[];
  sectionIndex: number;
  itemIndex: number;
};

const FUSE_CONFIG: Fuse.IFuseOptions<SearchableItem> = {
  threshold: 0.3,
  keys: ["label", "sectionLabel", "keywords"],
};

function isItemActive(itemHref: string, currentPath: string): boolean {
  if (itemHref.includes("?")) {
    return currentPath === itemHref;
  }
  return currentPath.split("?")[0] === itemHref;
}

const ProgramReportSidebar = (): ReactElement => {
  const router = useRouter();
  const currentPath = router.asPath.split("#")[0];
  const isOverviewActive = router.pathname === "/reporting";
  const { useMockData, setUseMockData } = useReportingMockData();
  const { viewOverride, setViewOverride } = useReportingViewOverride();
  const { isStaff } = useIsStaff();

  const { data: savedReportsData } = useGetSavedReportsQuery();
  const taskListsEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const knowledgeCheckEnabled = useFeatureFlag(Ff.KnowledgeCheck);
  const askOpusEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);

  const navSections: NavSection[] = useMemo(() => {
    const savedReports = savedReportsData?.GetSavedReports ?? [];
    const customTablesSection: NavSection = {
      label: "Custom tables",
      icon: SidebarEditIcon,
      keywords: ["custom", "table", "report", "builder", "saved"],
      items: [...savedReports]
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((report) => ({
          label: report.title,
          href: `/reporting/builder?id=${report.id}`,
          inlined: true,
        })),
      createAction: {
        label: "Create new table",
        href: "/reporting/builder",
      },
    };
    const hiddenHrefs = new Set<string>();
    if (!taskListsEnabled) {
      hiddenHrefs.add("/reporting/task-list-completion-grid");
      hiddenHrefs.add("/reporting/all-task-submissions");
    }
    if (!auditsEnabled) {
      hiddenHrefs.add("/reporting/most-recent-audit-completion");
      hiddenHrefs.add("/reporting/all-audit-submissions");
    }
    if (!knowledgeCheckEnabled) hiddenHrefs.add("/reporting/knowledge-check");
    if (!askOpusEnabled) hiddenHrefs.add("/reporting/ask-opus-answers");

    const sections = [...getStaticNavSections(), customTablesSection];
    if (hiddenHrefs.size === 0) return sections;
    return sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => !hiddenHrefs.has(item.href)),
    }));
  }, [
    savedReportsData,
    taskListsEnabled,
    auditsEnabled,
    knowledgeCheckEnabled,
    askOpusEnabled,
  ]);

  const fuseInstance = useMemo(() => {
    const searchableItems: SearchableItem[] = navSections.flatMap(
      (section, sectionIndex) =>
        section.items.map((item, itemIndex) => ({
          label: item.label,
          sectionLabel: section.label,
          keywords: [...(item.keywords ?? []), ...(section.keywords ?? [])],
          sectionIndex,
          itemIndex,
        })),
    );
    return new Fuse(searchableItems, FUSE_CONFIG);
  }, [navSections]);

  const { collapsedSections, toggleSection, expandSection } =
    useExpandedSidebarSectionsStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [staffToolsOpen, setStaffToolsOpen] = useState(false);

  useEffect(() => {
    for (const section of navSections) {
      if (section.items.some((item) => isItemActive(item.href, currentPath))) {
        expandSection(section.label);
        break;
      }
    }
  }, [currentPath, navSections, expandSection]);

  const isSearching = searchQuery.trim().length > 0;

  const matchingItemsBySection = useMemo(() => {
    if (!isSearching) return null;
    const results = fuseInstance.search(searchQuery.trim());
    const map = new Map<number, Set<number>>();
    for (const result of results) {
      const { sectionIndex, itemIndex } = result.item;
      if (!map.has(sectionIndex)) {
        map.set(sectionIndex, new Set());
      }
      map.get(sectionIndex)!.add(itemIndex);
    }
    return map;
  }, [searchQuery, isSearching, fuseInstance]);

  return (
    <div className="flex min-h-full w-full flex-col gap-4 pr-2">
      <div className="flex flex-col gap-4">
        <div
          className={cn(
            "group/search relative flex h-8 items-center rounded-lg bg-black-alpha-3",
            "transition-[background-color,box-shadow] duration-150 ease-out",
            "hover:bg-black-alpha-4",
            "focus-within:bg-background focus-within:shadow-[0_0_0_1px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.06)] focus-within:hover:bg-background",
          )}
        >
          <SearchIcon
            className={cn(
              "pointer-events-none absolute left-2.5 size-4 shrink-0",
              "text-gray-9 transition-colors duration-150 ease-out",
              "group-focus-within/search:text-gray-12",
            )}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports"
            className={cn(
              "min-w-0 flex-1 border-none bg-transparent pl-8 pr-8 text-[13px] leading-none text-gray-12 outline-none",
              "placeholder:text-gray-9",
            )}
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchQuery("")}
              className={cn(
                "absolute right-1.5 flex size-6 items-center justify-center rounded-md",
                "text-gray-9 transition-colors duration-100 ease-out",
                "hover:bg-black-alpha-3 hover:text-gray-12",
              )}
            >
              <XIcon className="size-3.5" />
            </button>
          )}
        </div>
        {!isSearching && (
          <Link
            href={{ pathname: "/reporting", query: { tab: "organization" } }}
            className={cn(
              "flex h-7 items-center gap-1.5 rounded-lg px-1.5 no-underline",
              isOverviewActive
                ? "!bg-black-alpha-2 text-foreground"
                : "text-gray-11 hover:!bg-black-alpha-2 hover:text-foreground",
            )}
          >
            <GaugeIcon className="size-4 shrink-0" />
            <Text variant="compact-description">Overview</Text>
          </Link>
        )}

        {isSearching && matchingItemsBySection?.size === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Text variant="fine-print-description" className="text-gray-9">
              No results found
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {navSections.map((section, sectionIndex) => {
              const matchingItems = matchingItemsBySection?.get(sectionIndex);
              if (isSearching && !matchingItems) return null;

              const isExpanded =
                isSearching || !collapsedSections.has(section.label);
              const visibleItems = isSearching
                ? section.items.filter((_, i) => matchingItems!.has(i))
                : section.items;

              return (
                <div key={section.label} className="flex flex-col gap-0.5">
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="flex items-center gap-[7px] rounded-md px-1.5 py-[5px]"
                  >
                    <Text variant="fine-print-title" className="text-gray-9">
                      {section.label}
                    </Text>
                    <CaretDownFillIcon
                      className={cn(
                        "size-2 text-gray-9 transition-transform",
                        !isExpanded && "-rotate-90",
                      )}
                    />
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col gap-0.5">
                      {section.createAction && (
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="mb-1.5 self-start"
                        >
                          <a
                            href={section.createAction.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <PlusIcon />
                            {section.createAction.label}
                          </a>
                        </Button>
                      )}
                      {visibleItems.map((item) => {
                        const isActive = isItemActive(item.href, currentPath);
                        const itemClasses = cn(
                          "flex h-7 items-center gap-1.5 rounded-lg px-1.5 no-underline",
                          isActive
                            ? "!bg-black-alpha-2 text-foreground"
                            : "text-gray-11 hover:!bg-black-alpha-2 hover:text-foreground",
                        );

                        if (item.inlined) {
                          return (
                            <Link
                              key={item.label}
                              href={item.href as LinkProps["href"]}
                              className={itemClasses}
                            >
                              <section.icon className="size-4 shrink-0" />
                              <Text
                                variant="compact-description"
                                ellipsis
                                ellipsisParentFlexDirection="row"
                              >
                                {item.label}
                              </Text>
                            </Link>
                          );
                        }

                        return (
                          <a
                            key={item.label}
                            href={item.href}
                            className={itemClasses}
                          >
                            <section.icon className="size-4 shrink-0" />
                            <Text
                              variant="compact-description"
                              ellipsis
                              ellipsisParentFlexDirection="row"
                            >
                              {item.label}
                            </Text>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isOverviewActive && isStaff && (
        <div className="mt-auto">
          <button
            type="button"
            onClick={() => setStaffToolsOpen((v) => !v)}
            className="flex w-full items-center gap-1 rounded-md px-1.5 py-1 text-gray-7 hover:text-gray-9"
          >
            <Text variant="fine-print-description" className="text-[inherit]">
              Staff tools
            </Text>
            <CaretDownFillIcon
              className={cn(
                "size-2 transition-transform",
                !staffToolsOpen && "-rotate-90",
              )}
            />
          </button>
          {staffToolsOpen && (
            <div className="flex flex-col gap-1.5 pt-1 pl-1">
              <div className="flex items-center justify-between px-1.5">
                <Text variant="fine-print-description" className="text-gray-7">
                  View as
                </Text>
                <Select
                  value={viewOverride}
                  onValueChange={(value) =>
                    setViewOverride(value as ReportingViewOverride)
                  }
                >
                  <SelectTrigger
                    aria-label="View as"
                    className="h-6 w-[130px] border-gray-3 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Admin</SelectItem>
                    <SelectItem value="single">Single-unit manager</SelectItem>
                    <SelectItem value="multi">Multi-unit manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between px-1.5">
                <Text variant="fine-print-description" className="text-gray-7">
                  Use mock data
                </Text>
                <Switch
                  aria-label="Use mock data"
                  checked={useMockData}
                  onCheckedChange={setUseMockData}
                  size="sm"
                  className="border-gray-3 shadow-none data-[state=checked]:border-gray-7 data-[state=checked]:bg-gray-7"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProgramReportSidebar;
